/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { CustomerApiService } from './customer-api.service';
import { FeatureFlagsService } from './feature-flags.service';
import { UserSettingsApiService } from './user-settings-api.service';

@Injectable({
  providedIn: 'root',
})
export class PortfolioApiService {
  private readonly _portfolios$ = new BehaviorSubject<any[]>([]);
  private readonly _activePortfolio$ = new BehaviorSubject<any>(null);

  readonly portfolios$: Observable<any[]> = this._portfolios$.asObservable();
  readonly activePortfolio$: Observable<any> = this._activePortfolio$.pipe(
    distinctUntilChanged(
      (a, b) =>
        a?.uid === b?.uid && a?.name === b?.name && a?.content === b?.content,
    ),
  );

  private portfoliosLoaded = false;

  constructor(
    private api: CustomerApiService,
    private featureFlagsService: FeatureFlagsService,
    private userSettingsApi: UserSettingsApiService,
  ) {}

  get portfolios(): any[] {
    return this._portfolios$.value;
  }

  get activePortfolio(): any {
    return this._activePortfolio$.value;
  }

  /** Active portfolio's entity content, or `[]` if no portfolio is active. */
  get activePortfolioContent(): any[] {
    return this._activePortfolio$.value?.content ?? [];
  }

  setActivePortfolio(folio: any): void {
    this._activePortfolio$.next(folio);
  }

  invalidateCache(): void {
    this.portfoliosLoaded = false;
    this._portfolios$.next([]);
    this._activePortfolio$.next(null);
  }

  portFolio(uid: string): Observable<any> {
    return this.api.get('/v4/portfolio', { uid });
  }

  portFolios(force = false): Observable<any[]> {
    if (!force && this.portfoliosLoaded) {
      return of(this._portfolios$.value);
    }
    return this.api
      .get<{ hits: any[] }>('/v4/portfolio/all', { _: String(Date.now()) })
      .pipe(
        map((res) => res.hits),
        tap((list) => {
          this._portfolios$.next(list);
          this.portfoliosLoaded = true;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
  }

  getAggregatedPortfolios(): Observable<any> {
    return this.api.get('/v1/aggregated_portfolios');
  }

  newFolio(name: string, type = 'regular'): Observable<any> {
    const path =
      type === 'aggregated'
        ? '/v1/aggregated_portfolios/create'
        : '/v3/portfolio/new';
    return this.withRefetch(this.api.get(path, { name }));
  }

  renamePortfolio(name: string): Observable<any> {
    const folio = this._activePortfolio$.value;
    const req$ =
      folio && folio.is_aggregated
        ? this.api.get(`/v1/aggregated_portfolios/${folio.uid}/rename`, {
            new_name: name,
          })
        : this.api.get('/v3/portfolio/rename', {
            uid: folio.uid,
            new_name: name,
          });
    return this.withRefetch(req$);
  }

  deletePortfolio(id: string | null = null): Observable<any> {
    const folio = this._activePortfolio$.value;
    const targetId = id ?? folio?.uid;
    const req$ =
      folio && folio.is_aggregated
        ? this.api.delete(`/v1/aggregated_portfolios/${targetId}`)
        : this.api.get('/v3/portfolio/delete', { uid: targetId });
    return this.withRefetch(
      req$.pipe(
        tap(() => {
          this.clearCustomDefaultIfMatches(targetId);
          if (folio?.uid === targetId) {
            this.setActivePortfolio(null);
          }
        }),
      ),
    );
  }

  addEntity(content: any): Observable<any> {
    return this.withRefetch(
      this.api.post('/v4/portfolio/add', {
        portfolio_ids: [this._activePortfolio$.value?.uid],
        content,
      }),
    );
  }

  addEntitiesToPortfolios(content: any, portfoliosIds: any[]): Observable<any> {
    return this.withRefetch(
      this.api.post('/v4/portfolio/add', {
        portfolio_ids: portfoliosIds,
        content,
      }),
    );
  }

  aggregatePortfolios(content: any, id: string): Observable<any> {
    return this.withRefetch(
      this.api.post('/v1/aggregated_portfolios/update', {
        uid: id,
        content,
      }),
    );
  }

  deleteEntity(entity: any): Observable<any> {
    return this.withRefetch(
      this.api.get('/v3/portfolio/delete', {
        uid: this._activePortfolio$.value?.uid,
        entry: entity,
      }),
    );
  }

  setFolios(data: any): Observable<string> {
    return this.withRefetch(this.api.postText('/v3/portfolio/add_bulk', data));
  }

  getSharedPortfolios(): Observable<any> {
    return this.api.get('/v1/shared_portfolios/mine');
  }

  createSharedPortfolio(id: string, mode: string): Observable<any> {
    return this.api.get('/v1/shared_portfolios/create', {
      portfolio_id: id,
      mode,
    });
  }

  sharePortfolio(id: string, users: any): Observable<any> {
    return this.api.post(`/v1/shared_portfolios/${id}/share`, { users });
  }

  unsharePortfolio(id: string, email: string): Observable<any> {
    return this.api.get(`/v1/shared_portfolios/${id}/unshare`, {
      user: email,
    });
  }

  deactivateSharedPortfolio(id: string): Observable<any> {
    return this.api.get(`/v1/shared_portfolios/${id}/deactivate`);
  }

  activateSharedPortfolio(id: string): Observable<any> {
    return this.api.get(`/v1/shared_portfolios/${id}/activate`);
  }

  getSharedPortfolioUsers(id: string): Observable<{ users: any }> {
    return this.api
      .get<{ shared_with: any }>(`/v1/shared_portfolios/${id}`)
      .pipe(map((resp) => ({ users: resp.shared_with })));
  }

  removeSharedPortfolio(id: string): Observable<any> {
    return this.api.delete(`/v1/shared_portfolios/${id}`);
  }

  /**
   * Wrap a mutating HTTP call so that the portfolio list is refreshed from the
   * server after success. Server stays authoritative for sort order, sharing
   * state, content enrichment.
   */
  private withRefetch<T>(req$: Observable<T>): Observable<T> {
    return req$.pipe(
      switchMap((res) => this.portFolios(true).pipe(map(() => res))),
    );
  }

  private clearCustomDefaultIfMatches(
    targetId: string | null | undefined,
  ): void {
    if (!targetId) return;
    if (targetId !== this.featureFlagsService.customDefaultPortfolio) return;
    this.featureFlagsService.customDefaultPortfolio = '';
    delete this.featureFlagsService.cockpitSettings?.['customDefaultPortfolio'];
    const newCustomSettings = {
      ...(this.featureFlagsService.cockpitSettings ?? {}),
    };
    this.userSettingsApi.setCustomSettings(newCustomSettings).subscribe();
  }
}
